import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../firebase'

const AuthContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs beside its context
export function useAuth() {
  return useContext(AuthContext);
}


async function getCustomClaims() {
  const user = auth.currentUser;
  if (!user) return null;

  const idTokenResult = await user.getIdTokenResult();
  return idTokenResult.claims;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [eventRoles, setEventRoles] = useState([])
  const [isSysAdmin, setIsSysAdmin] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null)
        setProfile(null)
        setLoading(false)
        setEventRoles([])
        setIsAdmin(false)
        setIsSysAdmin(false)
        return
      }

      setLoading(true)
      setError(null)
      setUser(firebaseUser)


      try {
        setEventRoles([])
        if (!firebaseUser.isAnonymous) {
          const claims = await getCustomClaims();
          const sysadmin = claims?.admin || false;
          setIsSysAdmin(sysadmin);
          const snap = await getDoc(doc(db, 'users', firebaseUser.uid))
          setProfile(
            snap.exists()
              ? { id: firebaseUser.uid, ...snap.data() }
              : { id: firebaseUser.uid, email: firebaseUser.email },
          )
          if (!sysadmin) {
            const q = query(
              collection(db, "adminusers"),
              where(firebaseUser.uid, "!=", false)
            );

            const snapshot = await getDocs(q);
            // Replace, never append: appending to the previous state carries one
            // user's roles into the next sign-in of the same tab, which would
            // route them by somebody else's role.
            setEventRoles(snapshot.docs.map((d) => ({ eventid: d.id, role: d.data()[firebaseUser.uid] })));
          }
        }
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    })

    return () => unsub()
  }, [])

  useEffect(() => {
    setIsAdmin(isSysAdmin || eventRoles.length > 0)
  }, [eventRoles, isSysAdmin])

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password)
  const logout = () => signOut(auth)

  const value = useMemo(() => {
    const hasEventAccess = (eventId) =>
      isSysAdmin || !!eventRoles?.find(role => role.eventid === eventId)

    const isWeddingAdmin = (eventId) =>
      !!eventRoles?.find(role => role.eventid === eventId && role.role === 'weddingadmin')

    const getEventRole = (eventId) =>
      eventRoles?.find(role => role.eventid === eventId)?.role ?? null

    // A moderator has no business in an event's settings: every route that would
    // show them sends them to that event's moderation screen instead.
    const isModeratorOnly = (eventId) =>
      !isSysAdmin && getEventRole(eventId) === 'moderator'

    return {
      user,
      profile,
      isSysAdmin,
      isAdmin,
      eventRoles,
      loading,
      error,
      login,
      logout,
      hasEventAccess,
      isWeddingAdmin,
      getEventRole,
      isModeratorOnly,
    }
  }, [user, profile, loading, error, isSysAdmin, eventRoles, isAdmin])

  // console.log("isSysAdmin", isSysAdmin)
  // console.log("eventRoles", eventRoles)
  // console.log("profile", profile);
  // console.log("user: ", user);

  return <AuthContext.Provider value={value}>
    {!loading && children}
  </AuthContext.Provider>
}

